import { useEffect, useMemo, useState } from "react";
import { MaterialReactTable, type MRT_ColumnDef } from "material-react-table";
import { StyledTableFirstSecond } from "../Book/TableBook";
import { Container } from "reactstrap";
import {
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Tooltip,
  TextField,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import axiosInstance from "../../Utils/axiosInstance";
import { backendbaseUrl } from "../../Utils/baseUrl";
import { format, parse } from "date-fns";
import Accordion from "react-bootstrap/Accordion";
import { CompactTextField } from "../../Components/SharedComponents/SharedComponents";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

// Data type for holidays
type HolidayData = {
  id: string;
  name: string;
  formatted_date: string;
};

const HolidayTable = () => {
  const [inputBackgroundColour, setInputBackgroundColour] = useState("");
  const [holidayTypeData, setHolidayTypeData] = useState<HolidayData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newHolidayName, setNewHolidayName] = useState("");
  const [newHolidayDates, setNewHolidayDates] = useState<Array<Date | null>>([
    null,
  ]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentEditData, setCurrentEditData] = useState<{
    id: string;
    holidayName: string;
    dateIndex: number;
    currentDate: string;
  } | null>(null);
  const [editDateValue, setEditDateValue] = useState<Date | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [datePickerState, setDatePickerState] = useState<{
    column: string | null;
    rowIndex: number | null;
    dateValue: string;
  }>({ column: null, rowIndex: null, dateValue: "" });

  // Validation state for form inputs
  const [validationErrors, setValidationErrors] = useState<{
    newHolidayName?: string;
    newHolidayDates?: string[];
  }>({});
  const [touchedFields, setTouchedFields] = useState<{
    newHolidayName?: boolean;
    newHolidayDates?: boolean[];
  }>({});
  const [editDateError, setEditDateError] = useState<string>("");

  // Validation functions
  const validateHolidayName = (value: string): string => {
    if (!value.trim()) return "Holiday name is required";
    if (value.length < 3) return "Holiday name must be at least 3 characters";
    return "";
  };

  const validateDate = (value: string | Date | null): string => {
    if (!value) return "Date is required";
    const date = typeof value === "string" ? parseDateFromApi(value) : value;
    if (isNaN(date.getTime())) return "Invalid date format";
    return "";
  };

  // Fetch holiday data
  const holidaysFetchData = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(
        `${backendbaseUrl}/paper-app/holidays/`
      );
      if (response.status === 200) {
        setHolidayTypeData(response.data.reverse()); // newest → oldest
      }
    } catch (error) {
      console.error("Error fetching holidays:", error);
      toast.error("Failed to fetch holidays. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    holidaysFetchData();
  }, []);

  // Date formatting utilities
  const formatDateForApi = (date: Date) => {
    try {
      return format(date, "dd-MM-yyyy");
    } catch (error) {
      console.error("Date formatting error:", error);
      return format(new Date(), "dd-MM-yyyy");
    }
  };

  const parseDateFromApi = (dateString: string) => {
    try {
      return parse(dateString, "dd-MM-yyyy", new Date());
    } catch (error) {
      console.error("Date parsing error:", error);
      return new Date();
    }
  };

  const formatDateForInput = (date: Date | null) => {
    return date ? format(date, "yyyy-MM-dd") : "";
  };

  const parseDateFromInput = (dateString: string): Date | null => {
    if (!dateString) return null;
    return new Date(dateString);
  };

  const handleAddMoreDate = () => {
    setNewHolidayDates([...newHolidayDates, null]);
    setValidationErrors((prev) => ({
      ...prev,
      newHolidayDates: [...(prev.newHolidayDates || []), "Date is required"],
    }));
    setTouchedFields((prev) => ({
      ...prev,
      newHolidayDates: [...(prev.newHolidayDates || []), false],
    }));
  };

  const handleRemoveDate = (index: number) => {
    if (newHolidayDates.length === 1) return;
    const updatedDates = [...newHolidayDates];
    updatedDates.splice(index, 1);
    setNewHolidayDates(updatedDates);

    const updatedErrors = [...(validationErrors.newHolidayDates || [])];
    updatedErrors.splice(index, 1);
    setValidationErrors((prev) => ({
      ...prev,
      newHolidayDates: updatedErrors,
    }));

    const updatedTouched = [...(touchedFields.newHolidayDates || [])];
    updatedTouched.splice(index, 1);
    setTouchedFields((prev) => ({
      ...prev,
      newHolidayDates: updatedTouched,
    }));
  };

  const handleDateChange = (newDate: string, index: number) => {
    const parsedDate = parseDateFromInput(newDate);
    const updatedDates = [...newHolidayDates];
    updatedDates[index] = parsedDate;
    setNewHolidayDates(updatedDates);

    const error = validateDate(parsedDate);
    const updatedErrors = [...(validationErrors.newHolidayDates || [])];
    updatedErrors[index] = error;
    setValidationErrors((prev) => ({
      ...prev,
      newHolidayDates: updatedErrors,
    }));

    const updatedTouched = [...(touchedFields.newHolidayDates || [])];
    updatedTouched[index] = true;
    setTouchedFields((prev) => ({
      ...prev,
      newHolidayDates: updatedTouched,
    }));
  };

  const handleSaveNewHoliday = async () => {
    const nameError = validateHolidayName(newHolidayName);
    const dateErrors = newHolidayDates.map((date) => validateDate(date));

    setValidationErrors({
      newHolidayName: nameError,
      newHolidayDates: dateErrors,
    });
    setTouchedFields({
      newHolidayName: true,
      newHolidayDates: newHolidayDates.map(() => true),
    });

    if (nameError || dateErrors.some((error) => error)) {
      toast.error("Please correct the form errors before saving.");
      return;
    }

    setIsLoading(true);
    try {
      const validDates = newHolidayDates.filter(
        (date): date is Date => date !== null
      );
      if (validDates.length === 0) {
        toast.error("At least one valid date is required.");
        return;
      }

      const tempHolidays: HolidayData[] = validDates.map((date, index) => ({
        id: `temp-${Date.now()}-${index}`,
        name: newHolidayName,
        formatted_date: formatDateForApi(date),
      }));

      setHolidayTypeData((prev) => [...prev, ...tempHolidays]);

      const promises = validDates.map((date) =>
        axiosInstance.post(`${backendbaseUrl}/paper-app/holidays/`, {
          name: newHolidayName,
          date: formatDateForApi(date),
        })
      );

      const responses = await Promise.all(promises);

      const newHolidays: HolidayData[] = responses.map((response, index) => ({
        id: response.data.id || `temp-${Date.now()}-${index}`,
        name: newHolidayName,
        formatted_date: formatDateForApi(validDates[index]),
      }));

      setHolidayTypeData((prev) => [
        ...prev.filter((item) => !item.id.startsWith("temp-")),
        ...newHolidays,
      ]);

      toast.success("Holiday added successfully!");
      holidaysFetchData();
      resetAddForm();
    } catch (error) {
      console.error("Error adding holiday dates:", error);
      toast.error("Failed to add holiday. Please try again.");
      setHolidayTypeData((prev) =>
        prev.filter((item) => !item.id.startsWith("temp-"))
      );
    } finally {
      setIsLoading(false);
    }
  };

  const resetAddForm = () => {
    setNewHolidayName("");
    setNewHolidayDates([null]);
    setShowAddForm(false);
    setValidationErrors({});
    setTouchedFields({});
  };

  const handleEditClick = (
    id: string,
    holidayName: string,
    dateIndex: number,
    currentDate: string
  ) => {
    setCurrentEditData({ id, holidayName, dateIndex, currentDate });
    const parsedDate = parseDateFromApi(currentDate);
    setEditDateValue(parsedDate);
    setEditDateError("");
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (id: string, name: string) => {
    setItemToDelete({ id, name });
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    setIsLoading(true);
    try {
      await axiosInstance.delete(
        `${backendbaseUrl}/paper-app/holidays/${itemToDelete.id}/`
      );
      setHolidayTypeData((prev) =>
        prev.filter((item) => item.id !== itemToDelete.id)
      );
      toast.success("Holiday deleted successfully!");
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } catch (error) {
      console.error("Error deleting holiday:", error);
      toast.error("Failed to delete holiday. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!currentEditData || !editDateValue) return;

    const error = validateDate(editDateValue);
    setEditDateError(error);

    if (error) {
      toast.error("Please correct the date error before saving.");
      return;
    }

    setIsLoading(true);
    try {
      await axiosInstance.put(
        `${backendbaseUrl}/paper-app/holidays/${currentEditData.id}/`,
        {
          name: currentEditData.holidayName,
          date: formatDateForApi(editDateValue),
        }
      );
      setHolidayTypeData((prev) =>
        prev.map((item) =>
          item.id === currentEditData.id
            ? {
                ...item,
                formatted_date: formatDateForApi(editDateValue),
              }
            : item
        )
      );
      toast.success("Holiday date updated successfully!");
      setEditDialogOpen(false);
      setCurrentEditData(null);
    } catch (error) {
      console.error("Error updating holiday date:", error);
      toast.error("Failed to update holiday date. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddDateClick = (column: string, rowIndex: number) => {
    setDatePickerState({ column, rowIndex, dateValue: "" });
  };

  const handleDatePickerChange = (
    value: string,
    column: string,
    rowIndex: number
  ) => {
    setDatePickerState((prev) => ({ ...prev, dateValue: value }));
  };

  const handleSaveDateFromPicker = async (column: string) => {
    if (!datePickerState.dateValue) {
      toast.error("Please select a date.");
      return;
    }

    const parsedDate = parseDateFromInput(datePickerState.dateValue);
    const error = validateDate(parsedDate);
    if (error || parsedDate === null) {
      toast.error(error || "Invalid date selected.");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axiosInstance.post(
        `${backendbaseUrl}/paper-app/holidays/`,
        {
          name: column,
          date: formatDateForApi(parsedDate),
        }
      );

      const newHoliday: HolidayData = {
        id: response.data.id || `temp-${Date.now()}`,
        name: column,
        formatted_date: formatDateForApi(parsedDate),
      };

      setHolidayTypeData((prev) => [...prev, newHoliday]);
      toast.success("New date added successfully!");
      setDatePickerState({ column: null, rowIndex: null, dateValue: "" });
    } catch (error) {
      console.error("Error adding new date:", error);
      toast.error("Failed to add new date. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const transformDataForTable = () => {
    // Get unique holiday names in the order they appear in holidayTypeData
    const uniqueHolidays = Array.from(
      new Set(holidayTypeData.map((item) => item.name))
    ); // Removed .sort((a, b) => a.localeCompare(b))

    const holidayDatesMap: Record<string, { id: string; date: string }[]> = {};
    uniqueHolidays.forEach((holiday) => {
      holidayDatesMap[holiday] = holidayTypeData
        .filter((item) => item.name === holiday)
        .map((item) => ({ id: item.id, date: item.formatted_date }));
    });

    const maxDatesCount = Math.max(
      ...Object.values(holidayDatesMap).map((dates) => dates.length),
      0
    );

    const rows = [];
    for (let i = 0; i <= maxDatesCount; i++) {
      const row: Record<string, any> = { index: i };
      uniqueHolidays.forEach((holiday) => {
        const dateInfo = holidayDatesMap[holiday][i];
        row[holiday] = dateInfo
          ? { id: dateInfo.id, date: dateInfo.date, index: i }
          : null;
      });
      rows.push(row);
    }

    return { columns: uniqueHolidays, rows };
  };

  const { columns: holidayColumns, rows: transformedData } =
    transformDataForTable();

  const columns = useMemo<MRT_ColumnDef<any>[]>(() => {
    return holidayColumns.map((holidayName) => ({
      accessorKey: holidayName,
      header: holidayName,
      enableEditing: false,
      Cell: ({ row }) => {
        const cellData = row.original[holidayName];
        const isLastDate =
          cellData &&
          holidayTypeData.filter((item) => item.name === holidayName).length ===
            cellData.index + 1;
        const isDatePickerVisible =
          datePickerState.column === holidayName &&
          datePickerState.rowIndex === row.original.index;

        if (isDatePickerVisible) {
          return (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  value={
                    datePickerState.dateValue
                      ? new Date(datePickerState.dateValue)
                      : null
                  }
                  onChange={(newValue) => {
                    const formattedValue = newValue
                      ? newValue.toISOString().split("T")[0] // format yyyy-MM-dd
                      : "";
                    handleDatePickerChange(
                      formattedValue,
                      holidayName,
                      row.original.index
                    );
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: {
                        "& .MuiInputBase-root": {
                          fontSize: "0.7rem",
                          height: "32px",
                        },
                        "& .MuiInputLabel-root": {
                          fontSize: "0.7rem",
                        },
                        width: 140,
                      },
                    },
                  }}
                />
              </LocalizationProvider>

              <IconButton
                size="small"
                color="primary"
                onClick={() => handleSaveDateFromPicker(holidayName)}
              >
                <SaveIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={() =>
                  setDatePickerState({
                    column: null,
                    rowIndex: null,
                    dateValue: "",
                  })
                }
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          );
        }

        if (!cellData) {
          if (row.original.index === 0) {
            return (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <span>-</span>
                <Tooltip title="Add Date">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() =>
                      handleAddDateClick(holidayName, row.original.index)
                    }
                    sx={{ padding: "4px" }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          }
          return "";
        }

        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <span>{cellData.date}</span>
            <Box sx={{ display: "flex" }}>
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={() =>
                    handleEditClick(
                      cellData.id,
                      holidayName,
                      cellData.index,
                      cellData.date
                    )
                  }
                  sx={{ padding: "4px" }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={() => handleDeleteClick(cellData.id, holidayName)}
                  sx={{ padding: "4px", color: "#d32f2f" }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {isLastDate && (
                <Tooltip title="Add Date">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() =>
                      handleAddDateClick(holidayName, row.original.index + 1)
                    }
                    sx={{ padding: "4px" }}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        );
      },
    }));
  }, [holidayColumns, holidayTypeData, datePickerState]);

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
      />
      <Accordion defaultActiveKey="0" className="table-ad__addInputs">
        <Accordion.Item eventKey="0">
          <Accordion.Header className="accordion_button">
            Add Holiday
          </Accordion.Header>
          <Accordion.Body>
            <div className="form_contain">
              <div
                className={`flex formWrapper ${
                  inputBackgroundColour !== "" ? "inputboxcustom" : ""
                }`}
                style={{ backgroundColor: inputBackgroundColour }}
              >
                <Box
                  sx={{
                    width: "100%",
                    p: { xs: 1, sm: 1 },
                    pt: "20px !important",
                    backgroundColor: "#ffff",
                    borderRadius: "4px",
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                      "& > *": {
                        minWidth: {
                          xs: "100%",
                          sm: "48%",
                          md: "23%",
                          lg: "15%",
                          xl: "7.5%",
                        },
                      },
                    }}
                  >
                    <CompactTextField
                      label="Holiday Name"
                      value={newHolidayName}
                      onChange={(value) => setNewHolidayName(value)}
                      name="newHolidayName"
                      required
                      error={!!validationErrors.newHolidayName}
                      helperText={
                        touchedFields.newHolidayName
                          ? validationErrors.newHolidayName
                          : ""
                      }
                      validateOnBlur={(value) => {
                        const error = validateHolidayName(value);
                        setValidationErrors((prev) => ({
                          ...prev,
                          newHolidayName: error,
                        }));
                        setTouchedFields((prev) => ({
                          ...prev,
                          newHolidayName: true,
                        }));
                        return error;
                      }}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                      sx={{
                        maxWidth: "238px",
                        "& .MuiInputBase-root": {
                          fontSize: "0.7rem",
                          height: "32px",
                        },
                        "& .MuiInputLabel-root": {
                          fontSize: "0.7rem",
                        },
                      }}
                    />
                    {newHolidayDates.map((date, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "flex-start",
                          justifyContent: "start",
                          gap: 0.5,
                          "& > *": {
                            minWidth: {
                              xs: "100%",
                              sm: "48%",
                              md: "23%",
                              lg: "15%",
                              xl: "7.5%",
                            },
                          },
                        }}
                      >
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DatePicker
                            label={`Date ${index + 1}`}
                            value={date}
                            onChange={(newValue: any) =>
                              handleDateChange(newValue, index)
                            }
                            // inputFormat="dd-MM-yyyy"       // how the date is displayed
                            // mask="__-__-____"
                            slotProps={{
                              textField: {
                                placeholder: "DD-MM-YY", // ✅ visible placeholder
                                required: true,
                                error:
                                  !!validationErrors.newHolidayDates?.[index],
                                helperText: touchedFields.newHolidayDates?.[
                                  index
                                ]
                                  ? validationErrors.newHolidayDates?.[index]
                                  : "",
                                size: "small",
                                sx: {
                                  "& .MuiInputBase-root": {
                                    fontSize: "0.7rem",
                                    height: "32px",
                                  },
                                  "& .MuiInputLabel-root": {
                                    fontSize: "0.7rem",
                                  },
                                },
                              },
                            }}
                          />
                        </LocalizationProvider>
                        <Stack direction="row" spacing={1}>
                          {newHolidayDates.length > 1 && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveDate(index)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                          {index === newHolidayDates.length - 1 && (
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={handleAddMoreDate}
                            >
                              <AddIcon />
                            </IconButton>
                          )}
                        </Stack>
                      </Box>
                    ))}
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveNewHoliday}
                      disabled={Boolean(
                        isLoading ||
                          !newHolidayName.trim() ||
                          validationErrors.newHolidayName ||
                          validationErrors.newHolidayDates?.some((e) => e)
                      )}
                      sx={{
                        height: { xs: "36px", sm: "32px" },
                        flex: { xs: "1 1 auto", sm: "0 0 auto" },
                        minWidth: { xs: "100%", sm: "100px" },
                        maxWidth: { sm: "150px" },
                        backgroundColor: "#00bcd4",
                        textTransform: "none",
                        fontSize: "0.75rem",
                        "&:hover": {
                          backgroundColor: "#008c9e",
                        },
                      }}
                    >
                      Save
                    </Button>
                  </Box>
                </Box>
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
      <div className="table-ad__wrapper ">
        <div className="table-ad__wrapperCard">
          <MaterialReactTable
            columns={columns}
            data={transformedData}
            enableTopToolbar={true}
            enableBottomToolbar={true}
            enablePagination={true}
            enableColumnFilters={true}
            state={{ isLoading }}
            enableDensityToggle={false}
            enableFilterMatchHighlighting={true}
            enableColumnFilterModes={true}
            manualFiltering={true}
            enableGlobalFilter={false}
            enableKeyboardShortcuts={true}
            createDisplayMode="row"
            enableColumnOrdering={true}
            enableColumnPinning={true}
            positionToolbarAlertBanner="bottom"
            enableColumnDragging={true}
            enableColumnResizing={true}
            columnResizeMode="onEnd"
            muiTopToolbarProps={({ table }) => ({
              sx: {
                backgroundColor: "#fff",
                padding: "8px",
                "& .MuiBox-root.css-wsew38": {
                  backgroundColor: "#fff",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                },
              },
              className: "top-toolbar-wrapper__new",
            })}
            muiTableContainerProps={({ table }) => ({
              sx: {
                backgroundColor: "#fff",
              },
              className: "table-container-wrapper__new",
            })}
            muiTableFooterProps={({ table }) => ({
              sx: {
                backgroundColor: "#fff",
              },
              className: "table-footer-wrapper__new",
            })}
            muiBottomToolbarProps={({ table }) => ({
              sx: {
                backgroundColor: "#fff",
                padding: "8px",
              },
              className: "bottom-toolbar-wrapper__new",
            })}
            muiTableHeadCellProps={({ table }) => ({
              sx: {
                border: "1px solid #e0e0e087",
                fontStyle: "normal",
                fontWeight: "normal",
              },
            })}
          />
        </div>
      </div>
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit Holiday Date</DialogTitle>
        <DialogContent className="custom-dialog-content">
          <Box sx={{ mt: 2 }}>
            <CompactTextField
              label="Date"
              type="date"
              value={formatDateForInput(editDateValue)}
              onChange={(value) => {
                const parsedDate = parseDateFromInput(value);
                setEditDateValue(parsedDate);
                const error = validateDate(parsedDate);
                setEditDateError(error);
              }}
              name="editHolidayDate"
              required
              error={!!editDateError}
              helperText={editDateError}
              validateOnBlur={(value) => {
                const parsedDate = parseDateFromInput(value);
                const error = validateDate(parsedDate);
                setEditDateError(error);
                return error;
              }}
              className="custom-dialog-input"
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
              sx={{
                width: "100%",
                "& .MuiInputBase-root": {
                  fontSize: "0.7rem",
                  height: "32px",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            color="primary"
            disabled={isLoading || !!editDateError || !editDateValue}
            sx={{
              height: { xs: "36px", sm: "32px" },
              flex: { xs: "1 1 auto", sm: "0 0 auto" },
              minWidth: { xs: "100%", sm: "100px" },
              maxWidth: { sm: "150px" },
              backgroundColor: "#00bcd4",
              textTransform: "none",
              fontSize: "0.75rem",
              "&:hover": {
                backgroundColor: "#008c9e",
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent className="custom-dialog-content">
          <Typography>Are you sure you want to delete this holiday?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            disabled={isLoading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const Example = () => {
  return (
    <div className="page-body">
      <Container fluid>
        <div className="table-ad">
          <HolidayTable />
        </div>
      </Container>
    </div>
  );
};

export default Example;
